import { FileText, BookOpen, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { FileEntry } from '../../types/memory';

interface FileTreeItemProps {
  file: FileEntry;
  selected: boolean;
  onSelect: (path: string) => void;
}

function formatSize(bytes?: number): string {
  if (bytes === undefined || bytes === null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatRelativeDate(dateStr?: string): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMs / 3600000);
  const diffDay = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default function FileTreeItem({ file, selected, onSelect }: FileTreeItemProps) {
  const navigate = useNavigate();
  const isMd = file.name.endsWith('.md');
  const Icon = isMd ? BookOpen : FileText;

  const handleOpenInDocs = (e: React.MouseEvent) => {
    e.stopPropagation();
    const vpsPath = '/var/lib/docker/volumes/ut-secondbrain_workspace-data/_data/' + file.path;
    navigate(`/docs?path=${encodeURIComponent(vpsPath)}`);
  };

  return (
    <button
      className={`w-full flex items-center gap-2 py-1.5 px-2 text-sm rounded-md transition-colors text-left group ${
        selected
          ? 'bg-accent-purple/10 text-accent-purple-light'
          : 'text-text-secondary hover:bg-bg-tertiary hover:text-text-primary'
      }`}
      onClick={() => onSelect(file.path)}
    >
      <Icon className="h-4 w-4 shrink-0" />
      <span className="truncate flex-1">{file.name}</span>
      {isMd && (
        <span
          onClick={handleOpenInDocs}
          className="text-text-muted hover:text-accent-purple-light opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
          title="Open in Docs viewer"
        >
          <ExternalLink className="h-3 w-3" />
        </span>
      )}
      <span className="text-xs text-text-muted shrink-0">
        {formatSize(file.size)}
      </span>
      <span className="text-xs text-text-muted shrink-0 hidden sm:inline">
        {formatRelativeDate(file.lastModified)}
      </span>
    </button>
  );
}
